import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Empty, Table, Tag, Tooltip, Typography,
} from 'antd';
import {
  PRICE_FIELDS,
  PRICING_SUMMARY_COLUMN_GROUPS,
  priceFieldGreyOut,
} from '../../../../util/pricing-fields';
import {
  sortSkusByContainerType,
  stripProductSkuShortLabel,
} from '../../../../util/sku-formatter';

const { Text } = Typography;

const priceFieldByKey = new Map(PRICE_FIELDS.map((pf) => [pf.key, pf]));

/** Display any stored price in the summary table with a $ prefix when missing. */
function formatDollarDisplay(value) {
  if (value == null) return null;
  const s = String(value).trim();
  if (s === '') return null;
  if (s.startsWith('$')) return s;
  return `$${s}`;
}

function renderPriceCell(pfKey, record) {
  const sku = record;
  if (priceFieldGreyOut(sku, pfKey)) {
    return <Text type="secondary">—</Text>;
  }
  const raw = record[pfKey];
  if (raw == null || raw === '') {
    return <Text type="secondary">—</Text>;
  }
  const formatted = formatDollarDisplay(raw);
  return formatted ? <Text>{formatted}</Text> : <Text type="secondary">—</Text>;
}

const PricingSummary = ({ skus, productName }) => {
  const orderedSkus = useMemo(
    () => (skus?.length ? sortSkusByContainerType(skus) : []),
    [skus],
  );

  const stripProductName = (name) => stripProductSkuShortLabel(name, productName || '');

  const columns = useMemo(() => {
    const skuCol = {
      title: 'SKU',
      dataIndex: 'name',
      key: 'name',
      fixed: 'left',
      width: 220,
      render: (name, record) => (
        <div>
          <Text strong>{stripProductName(name) || 'Unnamed SKU'}</Text>
          {record.isVenueSku && (
            <Tag color="purple" style={{ marginLeft: 8 }}>Venue</Tag>
          )}
          {record.isLegacy && (
            <Tag color="default" style={{ marginLeft: 6, fontSize: 10 }}>Legacy</Tag>
          )}
        </div>
      ),
    };

    const makeLeafColumn = (pf) => ({
      title: (
        <Tooltip title={pf.tooltip}>
          <span style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{pf.label}</span>
        </Tooltip>
      ),
      key: pf.key,
      width: 110,
      render: (_, record) => renderPriceCell(pf.key, record),
    });

    const priceGroups = PRICING_SUMMARY_COLUMN_GROUPS.map((group) => ({
      key: `gst-${group.subtitle.replace(/\s+/g, '-')}`,
      title: (
        <div style={{ textAlign: 'center', lineHeight: 1.25, padding: '2px 0' }}>
          <div style={{ fontSize: 12, fontWeight: 600 }}>{group.title}</div>
          <Text type="secondary" style={{ fontSize: 10, display: 'block' }}>
            {group.subtitle}
          </Text>
        </div>
      ),
      children: group.keys.map((key) => {
        const pf = priceFieldByKey.get(key);
        return makeLeafColumn(pf);
      }),
    }));

    return [skuCol, ...priceGroups];
  }, [productName]);

  if (!skus || skus.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="No SKUs yet — add SKUs in the SKUs tab."
      />
    );
  }

  return (
    <div style={{ width: '100%', minWidth: 0, overflowX: 'auto' }}>
      <Table
        dataSource={orderedSkus}
        columns={columns}
        rowKey={(row) => String(row.key ?? row.name)}
        pagination={false}
        size="small"
        bordered
        scroll={{ x: 'max-content' }}
      />
    </div>
  );
};

PricingSummary.propTypes = {
  productName: PropTypes.string,
  skus: PropTypes.arrayOf(PropTypes.object),
};

PricingSummary.defaultProps = {
  productName: null,
  skus: [],
};

export default PricingSummary;
